import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import ErrorUI from "../ErrorUI";
import { getStatusColor } from "./compareUtils";
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from "../../../../components/AccountDetailPolicy/accountDetailTypography";

const ACCENT = "#F98125";

const TAB_KEYS = [
  { key: "added", label: "Added", listKey: "addedResources", countKey: "addedResourceCount" },
  { key: "removed", label: "Removed", listKey: "removedResources", countKey: "removedResourceCount" },
  { key: "changed", label: "Status changed", listKey: "scoreChangedResources", countKey: "scoreChangedResourceCount" },
];

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "top",
};

const SUMMARY_CHIP_SX = {
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#475569",
  "& .MuiChip-label": { px: 1 },
};

const SEARCH_FIELD_SX = {
  width: "100%",
  mb: 1.5,
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const impactColor = (impact) => {
  const value = (impact || "").toUpperCase();
  if (value === "POSITIVE") return { color: "#166534", bg: "#dcfce7", border: "#bbf7d0" };
  if (value === "NEGATIVE") return { color: "#991b1b", bg: "#fee2e2", border: "#fecaca" };
  return { color: "#475569", bg: "#f1f5f9", border: "#e2e8f0" };
};

const statusChipSx = (status) => {
  const value = (status || "").toUpperCase();
  if (value === "FAILED") {
    return { bgcolor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" };
  }
  if (value === "PASSED") {
    return { bgcolor: "#ecfdf5", color: "#10b981", border: "1px solid #a7f3d0" };
  }
  if (value === "MANUAL") {
    return { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
  }
  return { bgcolor: "#f8fafc", color: getStatusColor(status) || "#64748b", border: "1px solid #e2e8f0" };
};

const StatusChip = ({ status }) => {
  if (!status) {
    return (
      <Typography sx={{ color: "#94a3b8", fontSize: "0.8125rem" }}>—</Typography>
    );
  }
  return (
    <Chip
      size="small"
      label={String(status).toUpperCase()}
      sx={{
        height: 22,
        fontSize: "0.6875rem",
        fontWeight: 700,
        borderRadius: "6px",
        ...statusChipSx(status),
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const resourceKey = (row, index) =>
  row?.serial_number != null
    ? `${row.serial_number}-${row.resource_arn || index}`
    : `${row?.resource_arn || "resource"}-${index}`;

const CompareResourcesDrawer = ({ open, onClose, checkTitle, onRetry }) => {
  const {
    isComparingCspmResources,
    compareCspmResourcesError,
    cspmCompareResources,
  } = useSelector((state) => state.cspm);
  const [tab, setTab] = useState("changed");
  const [search, setSearch] = useState("");

  const summary = cspmCompareResources?.summary || {};

  useEffect(() => {
    if (!open) return;
    setSearch("");
    const added = cspmCompareResources?.summary?.addedResourceCount || 0;
    const removed = cspmCompareResources?.summary?.removedResourceCount || 0;
    const changed = cspmCompareResources?.summary?.scoreChangedResourceCount || 0;
    if (changed > 0) setTab("changed");
    else if (added > 0) setTab("added");
    else if (removed > 0) setTab("removed");
    else setTab("changed");
  }, [open, cspmCompareResources]);

  const rows = useMemo(() => {
    const tabDef = TAB_KEYS.find((item) => item.key === tab) || TAB_KEYS[2];
    const list = cspmCompareResources?.[tabDef.listKey] || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((row) => {
      const resource = row.secondResource || row.firstResource || row;
      const haystack = [
        resource.resource_arn,
        resource.resourceArn,
        row.resourceArn,
        resource.Name,
        resource.region,
        resource.Service,
        resource.status,
        row.firstStatus,
        row.secondStatus,
        row.impact,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [cspmCompareResources, tab, search]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520, md: 720, lg: 880 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#fff",
          borderLeft: "1px solid #e2e8f0",
          boxShadow: "none",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#111827",
                letterSpacing: "-0.01em",
              }}
            >
              Resource comparison
            </Typography>
            {checkTitle ? (
              <Typography
                sx={{
                  color: "#64748b",
                  mt: 0.5,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {checkTitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close resource comparison"
            sx={{
              color: "#94a3b8",
              borderRadius: "8px",
              "&:hover": { color: "#64748b", backgroundColor: "#f1f5f9" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {isComparingCspmResources ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        ) : compareCspmResourcesError ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <ErrorUI error={compareCspmResourcesError} onRetry={onRetry} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: "12px",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              <Chip size="small" label={`Added ${summary.addedResourceCount ?? 0}`} sx={SUMMARY_CHIP_SX} />
              <Chip size="small" label={`Removed ${summary.removedResourceCount ?? 0}`} sx={SUMMARY_CHIP_SX} />
              <Chip
                size="small"
                label={`Status changed ${summary.scoreChangedResourceCount ?? 0}`}
                sx={SUMMARY_CHIP_SX}
              />
              <Chip
                size="small"
                label={`Positive ${summary.positiveImpactCount ?? 0}`}
                sx={{
                  ...SUMMARY_CHIP_SX,
                  bgcolor: "#dcfce7",
                  color: "#166534",
                  borderColor: "#bbf7d0",
                }}
              />
              <Chip
                size="small"
                label={`Negative ${summary.negativeImpactCount ?? 0}`}
                sx={{
                  ...SUMMARY_CHIP_SX,
                  bgcolor: "#fee2e2",
                  color: "#991b1b",
                  borderColor: "#fecaca",
                }}
              />
            </Box>

            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              sx={{
                minHeight: 40,
                mb: 1.5,
                flexShrink: 0,
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: 40,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "#64748b",
                  px: 1.5,
                },
                "& .Mui-selected": { color: `${ACCENT} !important` },
                "& .MuiTabs-indicator": {
                  backgroundColor: ACCENT,
                  height: 3,
                  borderRadius: "2px 2px 0 0",
                },
              }}
            >
              {TAB_KEYS.map((item) => (
                <Tab
                  key={item.key}
                  value={item.key}
                  label={`${item.label} (${summary[item.countKey] ?? 0})`}
                />
              ))}
            </Tabs>

            <TextField
              size="small"
              placeholder="Search resources"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={SEARCH_FIELD_SX}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={HEADER_CELL_SX}>Resource</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>Region</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>Service</TableCell>
                      <TableCell sx={HEADER_CELL_SX} align="center">
                        Status
                      </TableCell>
                      {tab === "changed" && (
                        <TableCell sx={HEADER_CELL_SX} align="center">
                          Impact
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={tab === "changed" ? 5 : 4} sx={{ ...BODY_CELL_SX, py: 4 }}>
                          <Typography
                            sx={{
                              color: "#94a3b8",
                              fontSize: "0.875rem",
                              textAlign: "center",
                              fontWeight: 500,
                            }}
                          >
                            No resources in this category.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, index) => {
                        const resource = row.secondResource || row.firstResource || row;
                        const firstStatus = row.firstStatus || row.firstResource?.status;
                        const secondStatus =
                          row.secondStatus || row.secondResource?.status || resource.status;
                        const statusChanged =
                          tab === "changed" &&
                          firstStatus &&
                          secondStatus &&
                          firstStatus !== secondStatus;
                        const impact = impactColor(row.impact);
                        return (
                          <TableRow
                            key={resourceKey(resource, index)}
                            hover
                            sx={{
                              "&:hover": { backgroundColor: "#f8fafc" },
                            }}
                          >
                            <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 360, wordBreak: "break-all" }}>
                              {resource.resource_arn || row.resourceArn || "—"}
                            </TableCell>
                            <TableCell sx={BODY_CELL_SX}>{resource.region || "—"}</TableCell>
                            <TableCell sx={BODY_CELL_SX}>{resource.Service || "—"}</TableCell>
                            <TableCell sx={BODY_CELL_SX} align="center">
                              {statusChanged ? (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.75,
                                    flexWrap: "nowrap",
                                    whiteSpace: "nowrap",
                                    lineHeight: 1,
                                  }}
                                >
                                  <StatusChip status={firstStatus} />
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "#94a3b8",
                                      fontSize: "0.75rem",
                                      fontWeight: 700,
                                      lineHeight: "22px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    →
                                  </Box>
                                  <StatusChip status={secondStatus} />
                                </Box>
                              ) : (
                                <StatusChip status={secondStatus} />
                              )}
                            </TableCell>
                            {tab === "changed" && (
                              <TableCell sx={BODY_CELL_SX} align="center">
                                {row.impact ? (
                                  <Chip
                                    size="small"
                                    label={String(row.impact).toUpperCase()}
                                    sx={{
                                      height: 22,
                                      fontSize: "0.6875rem",
                                      fontWeight: 700,
                                      borderRadius: "6px",
                                      bgcolor: impact.bg,
                                      color: impact.color,
                                      border: `1px solid ${impact.border}`,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ color: "#94a3b8", fontSize: "0.8125rem" }}>—</Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default CompareResourcesDrawer;
